// packages/ui/src/sidebar/SidebarPanelRegistry.ts
import type { ComponentType } from 'react';

export type SidebarPanelIcon = ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;

export interface SidebarPanelProps {
  /** Whether this panel is the active, visible one. */
  active: boolean;
}

export interface SidebarPanelDef {
  /** Stable id, e.g. 'explorer'. */
  id: string;
  /** Switcher tooltip / aria-label and panel header label. */
  title: string;
  /** Activity-bar switcher icon. */
  icon: SidebarPanelIcon;
  /** Switcher ordering (ascending). */
  order: number;
  /**
   * Where the switcher sits on the rail. 'top' (default) groups with the file
   * panels above the global file actions; 'bottom' places it after those
   * actions, behind a separator (e.g. the Plugins panel).
   */
  placement?: 'top' | 'bottom';
  /** Panel body. */
  Component: ComponentType<SidebarPanelProps>;
}

const defs = new Map<string, SidebarPanelDef>();
const listeners = new Set<() => void>();

// `getAll` is consumed via `useSyncExternalStore`, which requires a
// referentially-stable snapshot between mutations (a fresh array every call
// would loop forever). Cache the sorted snapshot; clear it on every mutation.
let snapshot: readonly SidebarPanelDef[] | null = null;

function emit(): void {
  snapshot = null;
  listeners.forEach((l) => l());
}

export const SidebarPanelRegistry = {
  register(def: SidebarPanelDef): () => void {
    if (defs.has(def.id)) {
      throw new Error(`SidebarPanelRegistry: duplicate panel id "${def.id}"`);
    }
    defs.set(def.id, def);
    emit();
    return () => {
      defs.delete(def.id);
      emit();
    };
  },
  getAll(): readonly SidebarPanelDef[] {
    if (snapshot === null) {
      snapshot = Array.from(defs.values()).sort((a, b) => a.order - b.order);
    }
    return snapshot;
  },
  getById(id: string): SidebarPanelDef | undefined {
    return defs.get(id);
  },
  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
};

/** Test-only: clear the registry. */
export function _resetSidebarRegistryForTest(): void {
  defs.clear();
  listeners.clear();
  snapshot = null;
}
